import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { Router } from '@angular/router';

import { MatIcon } from '@angular/material/icon';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';

import { formatDate } from '@lib/commons/util-functions/format-date.function';
import { MemberProfilesService } from '@lib/members/data-access';

import { TaskDataTargetComponent } from '../../../components/commons/task-data-target/task-data-target.component';
import { TASK_DATA } from '../../../core/contracts/task-data.contract';
import { HEALTH_CHECK_IN_RESULT_CHIP_CLASS } from '../../../core/models/health-check-in.const';
import { Task, TaskStatus, TaskType } from '../../../core/models/task.interface';
import * as TaskActions from '../../../core/store/task.actions';
import { MultilineTextComponent } from '../../commons/multiline-text/multiline-text.component';

@Component({
  selector: 'tasks-health-check-in-task-details',
  standalone: true,
  imports: [NgClass, MatIcon, TranslatePipe, TaskDataTargetComponent, MultilineTextComponent],
  templateUrl: './health-check-in-task-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col gap-6' },
})
export class HealthCheckInTaskDetailsComponent {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly memberProfilesService = inject(MemberProfilesService);

  protected readonly formatDate = formatDate;
  protected readonly taskStatus = TaskStatus;

  protected readonly task = inject<Task<TaskType.HealthCheckIn>>(TASK_DATA);
  protected readonly resultChipClass = HEALTH_CHECK_IN_RESULT_CHIP_CLASS[this.task.data.result];
  protected readonly chatLoading = signal<boolean>(false);

  protected navigateToMemberProfile(): void {
    this.router.navigate(['/members', this.task.data.memberId]);
  }

  // Chat threads are keyed by userId; the task only carries memberId, so resolve the profile first.
  protected goToChat(): void {
    this.chatLoading.set(true);

    this.memberProfilesService
      .getClientProfile(this.task.data.memberId)
      .pipe(take(1))
      .subscribe({
        next: profile => {
          const resolution = this.translate.instant('task.message.resolvedByChat');
          this.store.dispatch(TaskActions.resolveTask({ task: this.task, payload: { resolution } }));
          this.router.navigate(['/chat/threads'], { queryParams: { userId: profile.userId } });
        },
        error: () => this.chatLoading.set(false),
      });
  }
}
